/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import { Input } from "../Input";
import { useIconListContext } from "./IconListContext";
import { Icon } from "../Icon";
import { Button } from "../Button";
import { useClipboard } from "../../../hooks";

export interface IIconListValueProps {
	className?: string;
}

export const IconListValue: React.FC<IIconListValueProps> = ({ className }) => {
	const iconListCtx = useIconListContext();
	const { copyToClipboard, wasCopiedRecently } = useClipboard();
	const baseClass = BEM("edt-icon-list-value");

	const selectedIcon = iconListCtx?.selectedIconName;

	const handleCopy = useCallback(() => {
		copyToClipboard(selectedIcon);
	}, [ copyToClipboard, selectedIcon ]);

	const handleValueFocus = useCallback<React.MouseEventHandler<HTMLInputElement>>((event) => {
		const curTarget = event?.currentTarget;
		curTarget.setSelectionRange(0, curTarget.value?.length);
	}, []);

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("preview").toClassName()}>
				{selectedIcon && (
					<Icon name={selectedIcon} className={baseClass.element("preview-icon").toClassName()} />
				)}
			</div>

			<Input
				className={baseClass.element("input").toClassName()}
				value={selectedIcon || ""}
				readOnly
				onFocus={handleValueFocus}
			/>

			<Button
				disabled={!selectedIcon}
				startIcon={
					<Icon
						className={baseClass.element("btn-icon").toClassName()}
						name={wasCopiedRecently ? "mdi/check" : "mdi/content-copy"}
					/>
				}
				className={baseClass.element("btn").toClassName()}
				title="Copy icon name"
				onClick={handleCopy}
			>
				Copy name
			</Button>
		</div>
	);
};
